import { Link } from "react-router-dom";
import BookingLink from "@/components/BookingLink";
import { Button } from "@/components/ui/button";
import PageLayout from "@/components/PageLayout";
import LeadMagnetSection from "@/components/LeadMagnetSection";
import CTABand from "@/components/CTABand";
import FAQBlock from "@/components/FAQBlock";
import { BarChart3, Shield, Settings, Zap, TrendingUp, Eye, FileCheck, ArrowRight, ShieldCheck } from "lucide-react";
import outcomesPerformance from "@/assets/outcomes-performance.webp";

const featureCards = [
  {
    icon: ShieldCheck,
    title: "AI Governance & Responsible AI",
    description: "Policies, risk frameworks and assurance aligned to DfE, Ofqual and emerging UK AI regulation — so you can adopt AI safely, ethically and with board-level confidence.",
    link: "/ai-for-business",
  },
  {
    icon: BarChart3,
    title: "Apprenticeship Intelligence Platform (AIP)",
    description: "AI-powered dashboards integrating LMS and ePortfolio data for real-time insight into OTJH, reviews, progress and performance.",
    link: "/services",
  },
  {
    icon: Shield,
    title: "AI Governance & Compliance Framework",
    description: "AI use policies aligned to DfE/Ofqual guidance with risk management for bias, safeguarding and data use.",
    link: "/services",
  },
  {
    icon: Settings,
    title: "Quality Operating System (QOS)",
    description: "Full Ofsted-aligned IQA and quality framework including QA strategy, learner journey mapping and SAR/QIP support.",
    link: "/services",
  },
  {
    icon: Zap,
    title: "90-Day Apprenticeship Performance Reset",
    description: "Rapid diagnostics, OTJH recovery, caseload restructuring and targeted interventions with leadership dashboards.",
    link: "/services",
  },
];

const outcomeTiles = [
  { icon: TrendingUp, headline: "Stronger governance insight", description: "Real-time dashboards replace static spreadsheets for board-level oversight." },
  { icon: Eye, headline: "Faster interventions", description: "Predictive risk indicators flag issues before they become inspection findings." },
  { icon: FileCheck, headline: "Inspection-ready evidence", description: "Ofsted and AAF-aligned reporting generated automatically from live data." },
];

const homeFaqs = [
  { question: "What is QA 1st?", answer: "QA 1st is a premium consultancy for UK apprenticeship training providers. We combine strategic IQA expertise, AI-powered dashboards and Ofsted-aligned frameworks to help providers build intelligent quality systems." },
  { question: "Who are QA 1st's services designed for?", answer: "Our services are designed for CEOs, Quality Directors, Heads of Curriculum and Compliance Managers at apprenticeship training providers across the UK." },
  { question: "How do AI dashboards support Ofsted and AAF readiness?", answer: "Our Apprenticeship Intelligence Platform integrates data from your LMS and ePortfolio systems to generate real-time insight, predictive risk indicators and Ofsted/AAF-aligned reports — giving you inspection-ready evidence at all times." },
  { question: "What makes QA 1st different from other quality consultancies?", answer: "We go beyond advice. We partner with your teams to build skills and confidence, supported by structured e-learning, targeted workshops and bespoke dashboards that provide real-time insight, strengthen monitoring and support robust governance." },
  { question: "How does the Quality & Data Diagnostic work?", answer: "The diagnostic is a focused assessment of your quality systems, data maturity and Ofsted readiness. We deliver clear, prioritised recommendations you can act on immediately." },
  { question: "Can QA 1st help with safe use of AI in apprenticeships?", answer: "Yes. Our AI Governance & Compliance Framework provides policies, risk management, staff training and optional AI activity dashboards aligned to DfE and Ofqual guidance." },
];

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://qa1st.com/#organization",
      name: "QA 1st",
      url: "https://qa1st.com",
      description:
        "Premium UK consultancy for apprenticeship training providers combining IQA expertise, AI dashboards and Ofsted-aligned frameworks.",
      areaServed: "United Kingdom",
      sameAs: [],
    },
    {
      "@type": "WebSite",
      "@id": "https://qa1st.com/#website",
      url: "https://qa1st.com",
      name: "QA 1st",
      publisher: { "@id": "https://qa1st.com/#organization" },
      inLanguage: "en-GB",
    },
  ],
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: homeFaqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

const Index = () => {
  return (
    <PageLayout
      title="QA 1st | Intelligent Quality, AI Governance & Ofsted-Ready Systems"
      description="QA 1st combines IQA expertise, AI governance, AI dashboards and Ofsted-aligned frameworks to help UK apprenticeship providers build intelligent quality systems and drive better outcomes."
      jsonLd={organizationJsonLd}
    >
      {/* Hero */}
      <section className="bg-primary section-padding relative overflow-hidden">
        <img
          src="/hero-dashboard-mobile.webp"
          srcSet="/hero-dashboard-mobile.webp 800w, /hero-dashboard.webp 1400w"
          sizes="100vw"
          alt="AI-powered quality dashboard visualization"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1400}
          height={788}
        />
        <div className="container-narrow text-center max-w-4xl mx-auto relative z-10">
          <h1 className="text-primary-foreground mb-6">
            Intelligent quality systems, AI governance and Ofsted‑ready assurance for apprenticeship providers
          </h1>
          <p className="text-primary-foreground/70 text-lg md:text-xl mb-8 max-w-3xl mx-auto">
            We combine IQA expertise, AI dashboards and Ofsted‑aligned frameworks to turn fragmented data into real‑time intelligence, stronger governance and better outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" asChild>
              <BookingLink>Book a Free Diagnostic Call</BookingLink>
            </Button>
            <Button variant="hero-outline" asChild>
              <Link to="/contact">Request an AIP Dashboard Demo</Link>
            </Button>
            <Button variant="hero-outline" asChild>
              <Link to="/inclusion-cpd#waitlist">Join the CPD Waiting List</Link>
            </Button>
          </div>
          <p className="text-primary-foreground/50 text-sm mt-4">
            Trusted by apprenticeship providers across the UK
          </p>
        </div>
      </section>

      {/* Safeguarding Dashboard */}
      <section className="section-padding bg-card">
        <div className="container-narrow text-center">
          <h2 className="mb-4">Safeguarding Dashboard for Training Providers</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Our Safeguarding Dashboard gives providers real-time oversight of safeguarding concerns, Prevent duties and learner welfare — all in one place.
          </p>
          <div className="max-w-4xl mx-auto rounded-lg overflow-hidden shadow-lg border border-border">
            <video
              controls
              preload="none"
              className="w-full aspect-video"
              poster="/videos/safeguarding-thumbnail.webp"
            >
              <source src="/videos/safeguarding-dashboard.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Dashboard Walkthrough */}
      <section className="section-padding">
        <div className="container-narrow text-center">
          <h2 className="mb-4">See the aligned Ofsted Dashboard in action</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Watch how our Apprenticeship Intelligence Platform delivers real-time insight, predictive risk indicators and inspection-ready reporting.
          </p>
          <div className="max-w-4xl mx-auto rounded-lg overflow-hidden shadow-lg border border-border">
            <video
              controls
              preload="none"
              className="w-full aspect-video"
              poster="/videos/dashboard-thumbnail.webp"
            >
              <source src="/videos/dashboard-walkthrough.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="section-padding">
        <div className="container-narrow">
          <h2 className="text-center mb-4">What we do</h2>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
            Four integrated capabilities designed to transform quality, data and governance across your apprenticeship provision.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featureCards.map((card) => (
              <Link
                key={card.title}
                to={card.link}
                className="bg-card border border-border rounded-lg p-6 card-hover group"
              >
                <card.icon className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-lg font-semibold mb-2 group-hover:text-accent transition-colors">{card.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">{card.description}</p>
                <span className="text-accent text-sm font-medium inline-flex items-center gap-1">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why QA 1st */}
      <section className="section-padding bg-card">
        <div className="container-narrow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="mb-4">Why QA 1st?</h2>
              <p className="text-muted-foreground mb-6">
                Our approach goes beyond advice — we partner with your teams to build the skills and confidence needed to apply improvements effectively.
              </p>
              <ul className="space-y-4">
                {[
                  { bold: "Systems, not spreadsheets", text: "— automated, integrated quality infrastructure" },
                  { bold: "Insight, not static reporting", text: "— real-time dashboards that inform decisions" },
                  { bold: "Prevention, not reaction", text: "— predictive indicators that flag risk early" },
                  { bold: "Capability, not dependency", text: "— we build your team's skills, not reliance on us" },
                ].map((item) => (
                  <li key={item.bold} className="flex items-start gap-3">
                    <div className="h-2 w-2 rounded-full bg-accent mt-2 shrink-0" />
                    <span className="text-foreground">
                      <strong>{item.bold}</strong> {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "150+", label: "Providers supported" },
                { value: "98%", label: "Ofsted-ready" },
                { value: "40%", label: "Faster interventions" },
                { value: "24/7", label: "Dashboard access" },
              ].map((stat) => (
                <div key={stat.label} className="bg-background border border-border rounded-lg p-6 text-center">
                  <div className="text-3xl font-bold text-accent mb-1">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <h2 className="mb-4">Measurable outcomes</h2>
              <p className="text-muted-foreground">
                Our systems deliver tangible improvements across quality, governance and inspection readiness.
              </p>
            </div>
            <img
              src={outcomesPerformance}
              alt="Performance metrics and quality improvement visualization"
              className="rounded-lg shadow-lg"
              loading="lazy"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomeTiles.map((tile) => (
              <div key={tile.headline} className="bg-card border border-border rounded-lg p-6 text-center card-hover">
                <tile.icon className="h-10 w-10 text-accent mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{tile.headline}</h3>
                <p className="text-muted-foreground text-sm">{tile.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LeadMagnetSection />

      {/* FAQ */}
      <FAQBlock title="Questions about QA 1st" faqs={homeFaqs} />

      {/* Final CTA */}
      <CTABand />

      {/* FAQ JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </PageLayout>
  );
};

export default Index;
